import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './VerifyOTP.css';
import backgroundImage from '../../assets/images/Background.jpeg';
import logo from '../../assets/images/logo.png';
import { verifyOTP, setAuthToken, setRefreshToken, removeAuthToken, removeRefreshToken, resendOTP, getProfile } from '../../api/services/authService';
import { validateDashboardRoleAccess } from '../../utils/roleAccessCheck';
import { getAvailableDashboards } from '../../utils/roleRouting';
import { useAuth } from '../../context/AuthContext';
import RolePicker from '../../components/RolePicker/RolePicker';

const VerifyOTP = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login: authLogin } = useAuth();
  const [otpCode, setOtpCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSendingOTP, setIsSendingOTP] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [otpResendTimer, setOtpResendTimer] = useState(0);
  const [rolePickerRoles, setRolePickerRoles] = useState(null);
  const [rolePickerUserName, setRolePickerUserName] = useState(null);

  const phoneNumber = location.state?.phone || '';
  const rememberMe = location.state?.rememberMe || false;

  useEffect(() => {
    if (!phoneNumber) {
      navigate('/login');
    }
  }, [phoneNumber, navigate]);

  const handleSendOTP = useCallback(async () => {
    setIsSendingOTP(true);
    setMessage({ text: '', type: '' });

    try {
      const response = await resendOTP();
      if (response.status === 'success') {
        setOtpResendTimer(60);
        setMessage({ text: response.message || 'OTP sent to your registered phone', type: 'success' });
      } else {
        setMessage({ text: response.message || 'Failed to send OTP. Please try again.', type: 'error' });
      }
    } catch (error) {
      setMessage({ text: error.message || 'Failed to send OTP. Please try again.', type: 'error' });
    } finally {
      setIsSendingOTP(false);
    }
  }, []);

  useEffect(() => {
    if (phoneNumber && otpResendTimer === 0) {
      handleSendOTP();
    }
  }, [phoneNumber, otpResendTimer, handleSendOTP]);

  useEffect(() => {
    if (otpResendTimer > 0) {
      const timer = setTimeout(() => {
        setOtpResendTimer(otpResendTimer - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [otpResendTimer]);

  const handleRolePickerSelect = useCallback(async (dash) => {
    const roles = rolePickerRoles || [];
    try {
      const roleCheck = await validateDashboardRoleAccess(dash.uiRole, {
        userRoles: roles,
        hasAdminRole: roles.includes('ADMIN'),
        isAdminEmail: false,
      });

      if (!roleCheck.hasAccess) {
        removeAuthToken();
        removeRefreshToken();
        setRolePickerRoles(null);
        setMessage({ text: roleCheck.message || 'Access denied for the selected role.', type: 'error' });
        return;
      }

      setRolePickerRoles(null);
      navigate(dash.route);
    } catch (err) {
      setMessage({ text: err?.message || 'Failed to continue. Please try again.', type: 'error' });
    }
  }, [rolePickerRoles, navigate]);

  const handleVerifyOTP = async (e) => {
    e.preventDefault();

    if (!otpCode.trim() || otpCode.length !== 6) {
      setMessage({ text: 'Please enter a valid 6-digit OTP code', type: 'error' });
      return;
    }

    if (!phoneNumber) {
      setMessage({ text: 'Phone number not found', type: 'error' });
      return;
    }

    setIsVerifying(true);
    setMessage({ text: '', type: '' });

    try {
      let phone = phoneNumber.trim();
      if (!phone.startsWith('+')) {
        if (phone.startsWith('0')) {
          phone = '+254' + phone.substring(1);
        } else if (phone.startsWith('254')) {
          phone = '+' + phone;
        } else {
          phone = '+254' + phone;
        }
      }

      const verifyResponse = await verifyOTP(phone, otpCode);

      if (verifyResponse.status === 'success' && verifyResponse.data?.valid) {
        const token = verifyResponse.data?.token || verifyResponse.token;
        const refreshToken = verifyResponse.data?.refreshToken;

        if (token) setAuthToken(token);
        if (refreshToken) setRefreshToken(refreshToken);

        setMessage({ text: 'OTP verified successfully! Logging you in...', type: 'success' });

        // Fetch profile to obtain roles & user data.
        let profileRoles = [];
        let profileUser = null;
        try {
          const profileRes = await getProfile();
          const profileData = profileRes?.data || profileRes;
          profileUser = profileData?.user || profileData || null;
          profileRoles = profileData?.roles || profileUser?.roles || [];

          if (rememberMe && profileUser) {
            localStorage.setItem('current_user', JSON.stringify(profileUser));
          }
        } catch (profileError) {
          console.error('Profile fetch after OTP verification failed:', profileError);
        }

        const normalizedRoles = (Array.isArray(profileRoles) ? profileRoles : []).map((r) => String(r).toUpperCase());
        const dashboards = getAvailableDashboards(normalizedRoles);

        // Sync AuthContext so ProtectedRoute sees the user as authenticated.
        if (token) {
          authLogin(token, refreshToken, profileUser, normalizedRoles);
        }

        if (dashboards.length === 0) {
          removeAuthToken();
          removeRefreshToken();
          setMessage({ text: 'No dashboard is assigned to your account. Please contact support.', type: 'error' });
          return;
        }

        // Single role → route directly. Multiple → show role picker.
        if (dashboards.length === 1) {
          // Run role-access check then navigate.
          try {
            const roleCheck = await validateDashboardRoleAccess(dashboards[0].uiRole, {
              userRoles: normalizedRoles,
              hasAdminRole: normalizedRoles.includes('ADMIN'),
              isAdminEmail: false,
            });
            if (!roleCheck.hasAccess) {
              removeAuthToken();
              removeRefreshToken();
              setMessage({ text: roleCheck.message || 'Access denied.', type: 'error' });
              return;
            }
            setTimeout(() => navigate(dashboards[0].route), 800);
          } catch (err) {
            setMessage({ text: err?.message || 'Failed to continue.', type: 'error' });
          }
        } else {
          setRolePickerRoles(normalizedRoles);
          setRolePickerUserName(profileUser?.firstName);
        }
      } else {
        setMessage({ text: 'Invalid OTP code. Please try again.', type: 'error' });
        setOtpCode('');
      }
    } catch (error) {
      const errorMessage = error.message || 'OTP verification failed. Please try again.';
      setMessage({ text: errorMessage, type: 'error' });
      setOtpCode('');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  return (
    <div className="verify-otp-container">
      <div className="verify-otp-background-blur" style={{ backgroundImage: `url(${backgroundImage})` }}></div>
      <div className="verify-otp-overlay">
        <div className="verify-otp-wrapper">
          {/* Left Section - Welcome */}
          <div className="verify-otp-welcome-section">
            <div className="welcome-content">
              <img src={logo} alt="Tenant Score Logo" className="welcome-logo" />
              <div className="welcome-text">
                <span className="welcome-hello">Hello</span>
                <span className="welcome-main">welcome</span>
              </div>
              <div className="contact-separator"></div>
              <div className="contact-details-section">
                <h3 className="contact-title">Contact Details</h3>
                <p className="contact-address">P. O. Box 12345, Nairobi, Kenya</p>
                <div className="contact-info">
                  <span className="contact-label">Email:</span>
                  <a href="mailto:info@tenantscore.com" className="contact-value contact-link">info@tenantscore.com</a>
                </div>
                <div className="contact-info">
                  <span className="contact-label">Tel:</span>
                  <a href="tel:+254700000000" className="contact-value contact-link">+254 700 000 000</a>
                  <span className="contact-separator-inline">|</span>
                  <span className="contact-label">Cell:</span>
                  <a href="tel:+254711000000" className="contact-value contact-link">+254 711 000 000</a>
                </div>
                <div className="contact-info">
                  <span className="contact-label">WhatsApp:</span>
                  <a href="https://wa.me/254722000000" className="contact-value contact-link">+254 722 000 000</a>
                </div>
              </div>
            </div>
          </div>

          {/* Right Section - OTP Form */}
          <div className="verify-otp-form-section">
            <form className="verify-otp-form" onSubmit={handleVerifyOTP}>
              <h2 className="form-title">Verify Phone Number</h2>
              <p className="otp-subtitle">
                Please enter the 6-digit OTP code sent to {phoneNumber}
              </p>

              {message.text && (
                <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-error'}`}>
                  {message.text}
                </div>
              )}

              <div className="form-group">
                <label htmlFor="otp">OTP Code</label>
                <input
                  type="text"
                  id="otp"
                  className="form-input"
                  placeholder="Enter 6-digit OTP"
                  value={otpCode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setOtpCode(value);
                  }}
                  maxLength={6}
                  required
                />
              </div>

              <div className="otp-resend-section">
                {otpResendTimer > 0 ? (
                  <p className="otp-timer">
                    Resend OTP in {otpResendTimer} seconds
                  </p>
                ) : (
                  <button
                    type="button"
                    className="resend-otp-btn"
                    onClick={handleSendOTP}
                    disabled={isSendingOTP}
                  >
                    {isSendingOTP ? 'Sending...' : 'Resend OTP'}
                  </button>
                )}
              </div>

              <div className="form-buttons">
                <button type="button" className="btn-back" onClick={handleBackToLogin}>
                  Back to Login
                </button>
                <button type="submit" className="btn-back" disabled={isVerifying || otpCode.length !== 6}>
                  {isVerifying ? 'Verifying...' : 'Verify OTP'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {rolePickerRoles && (
        <RolePicker
          roles={rolePickerRoles}
          onSelect={handleRolePickerSelect}
          userName={rolePickerUserName}
        />
      )}
    </div>
  );
};

export default VerifyOTP;
